import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Connection } from "@solana/web3.js";
import { NETWORKS, NetworkChoice, makeConnection, defaultNetworkKey } from "./lib/network";
import type { ConnectedWallet } from "./lib/wallet";
import { DEFAULT_FEES, FeeSettings } from "./lib/tx";
import type { LaunchSnapshot } from "./lib/guide";

export interface LogLine {
  t: number;
  level: "info" | "ok" | "warn" | "error";
  text: string;
}

/** The token this console is currently operating on. */
export interface WorkingToken {
  mint: string;
  symbol: string;
  decimals: number;
  poolId?: string;
  lpMint?: string;
}

interface Ctx {
  net: NetworkChoice;
  rpcUrl: string;
  connection: Connection;
  setNetwork(key: string): void;
  setRpcUrl(url: string): void;

  wallet: ConnectedWallet | null;
  setWallet(w: ConnectedWallet | null): void;

  fees: FeeSettings;
  setFees(f: FeeSettings): void;

  token: WorkingToken | null;
  setToken(t: WorkingToken | null): void;

  /**
   * Published by the Launch panel while it is mounted, so the walkthrough can
   * describe the form without owning it. Null when Launch has never been
   * opened — which the walkthrough reports honestly rather than guessing.
   */
  launchStatus: LaunchSnapshot | null;
  setLaunchStatus(s: LaunchSnapshot | null): void;

  log: LogLine[];
  say(level: LogLine["level"], text: string): void;
  clearLog(): void;
}

const AppCtx = createContext<Ctx | null>(null);

export function AppProvider({ children }: { children: React.ReactNode }) {
  // Not a constant: localnet is unreachable from an https origin (mixed
  // content), so a deployed console must not start there. See
  // defaultNetworkKey.
  const [netKey, setNetKey] = useState(() =>
    defaultNetworkKey(typeof window === "undefined" ? "http:" : window.location.protocol)
  );
  const [rpcOverride, setRpcOverride] = useState<Record<string, string>>({});
  const [wallet, setWallet] = useState<ConnectedWallet | null>(null);
  const [fees, setFees] = useState<FeeSettings>(DEFAULT_FEES);
  const [token, setToken] = useState<WorkingToken | null>(null);
  const [log, setLog] = useState<LogLine[]>([]);
  const [launchStatus, setLaunchStatus] = useState<LaunchSnapshot | null>(null);

  const net = NETWORKS.find((n) => n.key === netKey) ?? NETWORKS[0];
  const rpcUrl = rpcOverride[netKey] ?? net.rpcUrl;

  const connection = useMemo(
    () => (rpcUrl ? makeConnection(rpcUrl) : makeConnection("http://127.0.0.1:8899")),
    [rpcUrl]
  );

  const say = useCallback((level: LogLine["level"], text: string) => {
    setLog((prev) => [...prev.slice(-499), { t: Date.now(), level, text }]);
  }, []);

  const value: Ctx = {
    net,
    rpcUrl,
    connection,
    setNetwork: (key) => {
      setNetKey(key);
      setToken(null);
    },
    setRpcUrl: (url) => setRpcOverride((p) => ({ ...p, [netKey]: url })),
    wallet,
    setWallet,
    fees,
    setFees,
    token,
    setToken,
    launchStatus,
    setLaunchStatus,
    log,
    say,
    clearLog: () => setLog([]),
  };

  return <AppCtx.Provider value={value}>{children}</AppCtx.Provider>;
}

export function useApp(): Ctx {
  const c = useContext(AppCtx);
  if (!c) throw new Error("useApp must be used inside AppProvider");
  return c;
}
